import { EVENTS } from "@/lib/eventConfig";
import useReveal from "@/hooks/useReveal";
import { buildIcs, downloadIcs } from "@/lib/ics";
import { CalendarPlus } from "lucide-react";

function EventCard({ event, index }) {
  const ref = useReveal();

  const handleAddToCalendar = () => {
    const ics = buildIcs({
      uid: `${event.key}-odysseas-omid@baptism-simou`,
      title: `Odysseas Omid · ${event.title}`,
      description: event.description || "",
      location: event.venue || "",
      start: event.start,
      end: event.end,
    });
    downloadIcs(`odysseas-${event.key}.ics`, ics);
  };

  return (
    <article
      ref={ref}
      className="reveal glass-card rounded-[2rem] sm:rounded-[2.5rem] p-7 sm:p-9 flex flex-col h-full"
      style={{ transitionDelay: `${index * 120}ms` }}
      data-testid={`event-card-${event.key}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="label-eyebrow">{event.label}</span>
        <span className="font-serif-display italic text-[#8FA092] text-sm text-right">
          {event.accent}
        </span>
      </div>

      <h3 className="font-serif-display text-3xl sm:text-4xl text-[#4A5346] mt-6 leading-tight">
        {event.title}
      </h3>

      {event.image && (
        <div
          className="mt-6 overflow-hidden rounded-2xl"
          data-testid={`event-image-${event.key}`}
        >
          <img
            src={event.image}
            alt={event.imageAlt || event.venue}
            loading="lazy"
            className="gallery-img w-full h-44 sm:h-52 object-cover"
          />
        </div>
      )}

      <div className="mt-6 space-y-1 text-[#757E70]">
        <p className="font-serif-display italic text-xl text-[#4A5346]">
          {event.date}
        </p>
        <p className="text-sm tracking-wider uppercase">{event.time}</p>
      </div>

      <div className="mt-6 pt-6 border-t border-[#E2DFD8]">
        <p className="text-[#4A5346] font-medium">{event.venue}</p>
        <p className="text-sm text-[#757E70] mt-1">{event.address}</p>
      </div>

      <p className="text-[#757E70] leading-relaxed mt-6 text-[15px]">
        {event.description}
      </p>

      {event.mapQuery && (
        <div className="mt-6" data-testid={`event-map-${event.key}`}>
          <div className="overflow-hidden rounded-2xl border border-[#E2DFD8]">
            <iframe
              title={`Map to ${event.venue}`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(
                event.mapQuery
              )}&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-56 sm:h-64"
              style={{ border: 0, filter: "saturate(0.85) contrast(0.95)" }}
              allowFullScreen
            />
          </div>
          {event.mapsLink && (
            <a
              href={event.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-xs tracking-[0.22em] uppercase text-[#8FA092] hover:text-[#6F8175] transition-colors"
              data-testid={`event-map-link-${event.key}`}
            >
              <span>Open in Google Maps</span>
              <span aria-hidden="true">→</span>
            </a>
          )}
        </div>
      )}

      {event.start && event.end && (
        <div className="mt-auto pt-8">
          <button
            type="button"
            onClick={handleAddToCalendar}
            data-testid={`event-add-calendar-${event.key}`}
            className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#4A5346] border border-[#8FA092]/40 hover:border-[#6F8175] hover:text-[#6F8175] rounded-full px-5 py-3 transition-colors duration-300"
          >
            <CalendarPlus className="w-4 h-4" strokeWidth={1.4} />
            <span>Add to Calendar</span>
          </button>
        </div>
      )}
    </article>
  );
}

// Flatten events so an event with a `secondary` becomes two side-by-side cards.
function flattenEvents(events) {
  const out = [];
  events.forEach((ev) => {
    out.push(ev);
    if (ev.secondary) {
      out.push({
        key: `${ev.key}-secondary`,
        label: ev.label,
        accent: ev.secondary.eyebrow || "Then",
        title: ev.secondary.title,
        date: ev.date,
        time: ev.secondary.time,
        venue: ev.secondary.venue,
        address: ev.secondary.address || "",
        description: ev.secondary.description,
        start: ev.secondary.start,
        end: ev.secondary.end,
        image: ev.secondary.image,
        imageAlt: ev.secondary.imageAlt,
        mapQuery: ev.secondary.mapQuery,
        mapsLink: ev.secondary.mapsLink,
      });
    }
  });
  return out;
}

export default function Itinerary() {
  const headerRef = useReveal();
  const cards = flattenEvents(EVENTS);
  return (
    <section
      id="itinerary"
      className="relative py-24 sm:py-32 lg:py-40 px-6"
      data-testid="itinerary-section"
    >
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className="reveal text-center max-w-2xl mx-auto">
          <p className="label-eyebrow">The Three Days</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#4A5346] mt-6">
            A gentle unfolding
          </h2>
          <p className="text-[#757E70] mt-6 leading-relaxed">
            Three days, each held with intention — beginning with a quiet
            welcome, rising into a warm-up night of music, and resting in a
            sacred ceremony followed by a celebration by the sea.
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {cards.map((ev, idx) => (
            <EventCard key={ev.key} event={ev} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
